import { useProductOfferStore } from "@expert/sales-tools";
import { expertWorkspaceWebSocketEventBus } from "@expert/websocket";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { useSessionId } from "@/sdk";

interface SalesEventPayload {
    status: string;
    offerId: string;
    salesProvider: string;
}

/**
 * Bridges websocket events to the leaderboard TanStack Query cache.
 *
 * The decoupled Leaderboard component (from @expert/sales-tools) reads from the
 * query cache but doesn't subscribe to websocket events directly. This hook runs
 * in Workspace.tsx to keep leaderboard data live.
 */
export function useLeaderboardWebSocketBridge() {
    const queryClient = useQueryClient();
    const { leaderboards } = usePartnerConfig();
    const salesLeaderboard = leaderboards.find((leaderboard) => leaderboard.metric === "sales");
    const sessionId = useSessionId();

    useEffect(() => {
        const callsSubscription = expertWorkspaceWebSocketEventBus.on("ws_calls-event", (payload) => {
            void queryClient.setQueryData(["calls-leaderboard"], payload);
        });

        const enrollmentSubscription = expertWorkspaceWebSocketEventBus.on("ws_enrollment-event", (payload) => {
            const enrollmentEventPayload = payload as SalesEventPayload;
            const products = useProductOfferStore.getState().getProducts(sessionId);
            const enrolledOffer = products.find((p) => p.id === enrollmentEventPayload.offerId);

            if (
                salesLeaderboard &&
                enrolledOffer?.productSku === salesLeaderboard.sku &&
                enrollmentEventPayload.status === "SUCCESS"
            ) {
                void queryClient.refetchQueries({
                    queryKey: ["sales-leaderboard"],
                });
            }
        });

        return () => {
            callsSubscription();
            enrollmentSubscription();
        };
    }, [queryClient, sessionId, salesLeaderboard]);
}
